import { FormEventHandler, FormHTMLAttributes, useState } from "react";
import { hasPluginPermission } from "components/utils/Permission";
import { useStoreState } from "ezStore";
import EntityHistoryRemarkDialog from "components/entityHistory/components/dialogs/EntityHistoryRemarkDialog";

type TrackedEntityFormProps = {
	/**
	 * Invoked when the user clicks save. Receives an `askForRemark` function that opens
	 * the remark dialog and returns a promise resolving to the trimmed remark, or `null`
	 * if the remark is empty or the plugin is disabled. The promise rejects
	 * (with no value) when the user closes the dialog via the "Cancel" button - callers must catch this to
	 * treat cancellation as a no-op.
	 */
	onSubmit: (askForRemark: () => Promise<string | null>) => FormEventHandler<HTMLFormElement>;
} & Omit<FormHTMLAttributes<HTMLFormElement>, "onSubmit">;

const TrackedEntityForm = ({
	onSubmit,
	children,
	...formProps
}: TrackedEntityFormProps) => {
	const session = useStoreState((store) => store.session);
	const remarkEnabled = hasPluginPermission(session, "revisionRemark");

	const [ promise, setPromise ] = useState<{ resolve: (remark: string | null) => void; reject: () => void } | null>(null);

	const askForRemark = () => {
		if (!remarkEnabled) {
			return Promise.resolve(null);
		}

		return new Promise<string | null>((resolve, reject) => {
			setPromise({
				resolve: (remark) => {
					setPromise(null);
					resolve(remark);
				},
				reject: () => {
					reject();
					setPromise(null);
				},
			});
		});
	};

	return (
		<>
			<form {...formProps} onSubmit={onSubmit(askForRemark)}>
				{children}
			</form>

			<EntityHistoryRemarkDialog promise={promise} />
		</>
	);
};

export default TrackedEntityForm;
